import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmSignUp, isTokensResponse, resendCode } from "@/lib/authClient";
import { clearPendingCredentials, takePendingCredentials } from "@/lib/pendingCredentials";
import { takePostAuthTarget } from "@/lib/postAuthRedirect";

const searchSchema = z.object({
  email: z.string().email().optional(),
  returnTo: z.string().optional(),
});

export const Route = createFileRoute("/confirm")({
  validateSearch: searchSchema,
  component: ConfirmPage,
});

function ConfirmPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { setTokensFromExchange } = useAuth();

  const [email, setEmail] = useState(search.email ?? "");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setOkMsg(null);
    setSubmitting(true);
    try {
      const emailLc = email.trim().toLowerCase();

      // BUG-8: attempt auto-signin bridge. If the user came here directly
      // from /signup in the same tab, the password is in module memory.
      // If they opened the email link in a different tab or reloaded, it
      // won't be — the endpoint accepts an optional password and degrades
      // gracefully by returning {} so we fall back to /login below.
      const pending = takePendingCredentials(emailLc);

      const resp = await confirmSignUp({
        email: emailLc,
        code,
        password: pending?.password,
      });

      if (isTokensResponse(resp)) {
        // Auto-signin succeeded — feed tokens into the auth context.
        // Funnel policy: a just-confirmed user has never bought, so unless
        // an explicit target was saved (came from /passes), land on /passes
        // — the funnel converges on pricing until they own a pass.
        await setTokensFromExchange(resp);
        const target = takePostAuthTarget() ?? search.returnTo ?? "/passes";
        void navigate({ to: target as never, replace: true });
        return;
      }

      // Fallback: confirmation succeeded but no auto-signin (either the
      // user didn't have a pending password or auto-signin degraded).
      // Send them to /login; its own funnel policy handles the landing.
      void navigate({
        to: "/login" as never,
        search: { email: emailLc, returnTo: search.returnTo ?? "/passes" } as never,
        replace: true,
      });
    } catch (e) {
      // Confirmation itself failed (bad code, expired, etc). Clear any
      // stale pending password since the user will now enter it manually.
      clearPendingCredentials();
      if (e instanceof ApiError) {
        setErrorMsg(e.problem.detail ?? "Confirmation failed. Check the code and try again.");
      } else {
        setErrorMsg("Confirmation failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    setErrorMsg(null);
    setOkMsg(null);
    setResending(true);
    try {
      await resendCode({ email: email.trim().toLowerCase() });
      setOkMsg("A new code has been sent to your inbox.");
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMsg(e.problem.detail ?? "Could not resend the code.");
      } else {
        setErrorMsg("Could not resend the code.");
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <AppShell>
      <section className="container py-16 md:py-20">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle className="font-serif text-3xl text-palm">Confirm your email</CardTitle>
            <CardDescription>
              We sent a 6-digit code to your inbox. Enter it below to activate your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-widest"
                  disabled={submitting}
                />
              </div>

              {errorMsg && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMsg}
                </div>
              )}
              {okMsg && (
                <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-foreground">
                  {okMsg}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" variant="accent" disabled={submitting || code.length !== 6}>
                {submitting ? "Confirming…" : "Confirm"}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resending || !email}
                  className="text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {resending ? "Sending…" : "Resend code"}
                </button>
                <Link to={"/login" as never} className="text-palm underline underline-offset-2">
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
